import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import {
    ClientManagerInstanceToken,
    ClientManagerInterface,
    ConfigConstants,
    ConfigService,
    ENDPOINTS,
    EnvironmentInstanceToken,
    FileReaderUtilities,
    FileStorageInjectionToken,
    FileStorageServiceInterface,
    LZStringService,
    OnlineOnlyService,
    PaceEnvironment,
    SyncQueueService,
    SynchronizationInstanceToken,
    SynchronizationInterface,
    UriUtilitiesService,
    UserMasterService,
    isDefined,
    Notification,
} from '@evorait/pace-core';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { Mutex } from 'async-mutex';
import moment from 'moment';
import { Observable, Observer, throwError } from 'rxjs';
import { catchError, mapTo, mergeMap, switchMap, tap } from 'rxjs/operators';
import { DocumentStateUpload } from '../store/state/document.state';
import { DocBoxListModel, ODocBoxUploadModel } from './docBox-odata.interface';
import { OdataDocumentsModel } from './document-odata.interface';
import { OdataFormsService } from './odata-forms.service';

@Injectable({
    providedIn: 'root',
})
export class DocBoxService {
    private docBoxMutex: Mutex;
    public constructor(
        @Inject(ClientManagerInstanceToken) protected readonly clientManager: ClientManagerInterface,
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        @Inject(SynchronizationInstanceToken) protected readonly synchronizationService: SynchronizationInterface,
        @Inject(FileStorageInjectionToken) private readonly fileStorage: FileStorageServiceInterface,
        @Inject(EnvironmentInstanceToken) private readonly environment: PaceEnvironment,
        private readonly syncQueueService: SyncQueueService,
        private readonly httpClient: HttpClient,
        private readonly uriUtilities: UriUtilitiesService,
        private readonly onlineOnlyService: OnlineOnlyService,
        private readonly configService: ConfigService,
        private readonly userMasterService: UserMasterService,
        private readonly odataFormsService: OdataFormsService,
        protected readonly LZString: LZStringService,
    ) {
        this.docBoxMutex = new Mutex();
    }

    public getDocBoxFiles(
        materialNumber: string,
        serialNumber: string,
        technicalIdentNo?: string,
        functionalLocation?: string,
    ): Observable<DocBoxListModel[]> {
        let query = ENDPOINTS.GetDocBoxListSet.toString();
        let filter = '';
        if (technicalIdentNo) {
            filter = `TechnicalIdent eq '${technicalIdentNo}'`;
        } else if (functionalLocation) {
            filter = `Funcloc eq '${functionalLocation}'`;
        } else if (materialNumber && serialNumber) {
            filter = `Material eq '${materialNumber}' and SerialNo eq '${serialNumber}'`;
        }
        query = this.uriUtilities.appendFilterValue(query, filter);
        return this.onlineOnlyService.get(query) as Observable<DocBoxListModel[]>;
    }

    public getDocument(docBoxId: string[]): Observable<OdataDocumentsModel[]> {
        let query = ENDPOINTS.GetDocBoxDocumentsSet.toString();
        const filter = `(${docBoxId.map((id) => `DocBoxId eq '${id}'`).join(' or ')})`;
        const format = '$format=json';

        query = this.uriUtilities.appendFilterValue(query, filter);
        query = this.uriUtilities.appendParameter(query, format);

        return this.onlineOnlyService.get(query) as Observable<OdataDocumentsModel[]>;
    }

    public saveDocument(data: OdataDocumentsModel): void {
        // if (this.clientManager.isIOS() || this.clientManager.isWindows()) {
        //     const fileName = `${data.ZipArchiveName}`;
        //     const directory = this.clientManager.isWindows()
        //         ? this.cordovaFile.dataDirectory
        //         : this.cordovaFile.documentsDirectory;
        //     const content = this.base64ToArrayBuffer(data.Documents);
        //     this.cordovaFile
        //         .writeFile(directory, fileName, content, { replace: true })
        //         .then(() => {
        //             const filePath = `${directory}${fileName}`;
        //             this.log.debug(`DocBoxService: saveDocument: opening file: ${filePath}`);
        //             let mimeType = '';
        //             mimeType = 'application/zip';
        //
        //             this.fileOpener.open(filePath, mimeType).catch((error: unknown) => {
        //                 this.log.info(
        //                     `DocBoxService: saveDocument: Error while opening file: ${JSON.stringify(error)}`,
        //                 );
        //             });
        //         })
        //         .catch((error) => {
        //             this.log.info(`DocBoxService: saveDocument: Error while writing file: ${JSON.stringify(error)}`);
        //         });
        // } else {
        const zipFile = document.createElement('a');
        zipFile.href = `data:zip;base64,${data.Documents}`;
        zipFile.download = data.ZipArchiveName;
        zipFile.click();
        // }
    }

    private base64ToArrayBuffer(base64: string): Blob {
        const byteCharacters = atob(base64);
        const byteNumbers = new Array(byteCharacters.length);

        for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
        }

        const byteArray = new Uint8Array(byteNumbers);
        return new Blob([byteArray], { type: 'application/zip' });
    }

    public uploadDocBoxFile(document: DocumentStateUpload, notification: Notification): Observable<boolean> {
        if (!isDefined(document.file)) {
            return throwError(() => 'No file to be uploaded');
        }
        const file = document.file;

        return this.readFile(document.file).pipe(
            switchMap((base64) => {
                const materialNumber = notification.equipmentSet[0].matnr;
                const serialNumber = notification.equipmentSet[0].sernr;
                const user = this.userMasterService.getSelectedUser();

                const oDataUploadModel: ODocBoxUploadModel = {
                    Attachment: base64,
                    ContentType: file.type, // is d efinitely available because of check in method start
                    DocumentType: document.docType,
                    FileName: file.name, // is d efinitely available because of check in method start
                    MaterialNumber: materialNumber,
                    SerialNumber: serialNumber,
                    Name: document.title,
                    ProtectionClass: document.protectionClass,
                    Printno: document.printNumber,
                    DocumentState: document.reportStatus,
                    SourceSystem: this.configService.getBackendName(),
                    Salesorg: user.salesOrg,
                    Plant: user.plant,
                    Country: user.country,
                    Notification: notification.identifier.qmNum,
                    NotificationType: notification.notificationType,
                    Orderno: document.orderNumber,
                    Funcloc: document.funcLoc,
                    EquipmentNumber: document.equipment,
                    StartDate: moment(document.date).utcOffset(0, true).format(ConfigConstants.formatSAPDate),
                };

                return this.postRequest(ENDPOINTS.DocboxUploadSet, oDataUploadModel).pipe(
                    tap((response) =>
                        this.log.info(`DocBoxService: uploadDocBoxFile: response "${JSON.stringify(response)}"`),
                    ),
                    mapTo(true),
                    catchError((error) => {
                        this.log.error(
                            `DocBoxService: uploadDocBoxFile: uploading file failed, error "${JSON.stringify(error)}"`,
                        );
                        return throwError(error);
                    }),
                );
            }),
        );
    }
    private readFile(file: File): Observable<string> {
        const reader = FileReaderUtilities.getFileReader();

        return new Observable((observer: Observer<string>) => {
            reader.readAsDataURL(file);
            reader.addEventListener('load', () => {
                observer.next(typeof reader.result === 'string' ? reader.result.replace(/.*;base64,/, '') : '');
                observer.complete();
            });
            reader.addEventListener('error', (event) => {
                observer.error(event);
            });
        });
    }

    protected postRequest(url: string, payload: ODocBoxUploadModel): Observable<unknown> {
        if (this.userMasterService.isPaceOnlineUser() || this.clientManager.isPaceFormsFI()) {
            return this.onlineOnlyService.post(url, payload);
        }
        return this.odataFormsService
            .deepInsertRequest(url, payload, payload.Name)
            .pipe(mergeMap(() => this.synchronizationService.sendOfflineData()));
    }
}
